import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { format } from "date-fns";
import { toastAfterReload } from "../../../utils/successToast";
import lessonsApi from "../../../apis/lessonsApi";

// Cancel a lesson, before or after it happened.
//
// Upcoming: it comes off the calendar and the student and teacher are
// emailed, with the note if there is one.
//
// Past (the owner clearing a lesson that didn't happen, FirstClass's "Clear
// session"): it was already counted as taken, so cancelling gives the
// student the lesson back, and nobody is emailed about last week. Only
// owners see this case; the server holds the same line.
//
// The note is optional. Stefan, call 6: "I don't have to give a reason."
//
// A weekly lesson asks one more thing. Cancelling this week's lesson used to
// end the series without saying so, because next week's is only made from a
// lesson still on the calendar. Now the default is this week only, and
// stopping the weekly lesson is a choice.
const firstName = (name) => (name || "").trim().split(/\s+/)[0] || "";

const CancelLessonModal = ({ isOpen, onClose, lesson_id, lesson = {}, onCancelled }) => {
  const [note, setNote] = useState("");
  const [keepWeekly, setKeepWeekly] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const now = new Date();
  const start = lesson.start_time ? new Date(lesson.start_time) : null;
  const end = lesson.end_time ? new Date(lesson.end_time) : null;
  // Counted: the lesson has ended (the completion job may not have run yet).
  // Started: nobody is emailed from this moment on, matching the server.
  const past = lesson.status === "completed" || Boolean(end && end <= now);
  const started = past || Boolean(start && start <= now);
  const weekly = Boolean(lesson.weekly_latest);
  const student = lesson.student_name || "This student";
  const first = firstName(lesson.student_name) || "They";
  const when = start ? ` on ${format(start, "EEE MMM d")} at ${format(start, "h:mm a")}` : "";

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await lessonsApi.cancelLesson(lesson_id, {
        lesson: { cancel_notes: note.trim(), ...(weekly ? { keep_weekly: keepWeekly } : {}) },
      });
      if (onCancelled) {
        onCancelled({ past });
      } else {
        toastAfterReload(past ? `Lesson cancelled. ${first} has it back.` : "Lesson cancelled");
        window.location.reload();
      }
    } catch (e) {
      setError(typeof e === "string" ? e : e?.error || "That lesson could not be cancelled. Try again.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{past ? "Cancel a lesson that already counted?" : "Cancel this lesson?"}</DialogTitle>
      <DialogContent>
        {past ? (
          <Typography style={{ fontSize: 14.5, lineHeight: "21px", marginBottom: 14 }} data-testid="cancel-lesson-explainer">
            {student}'s lesson{when} is in the past, so it was counted as taken.
            Cancelling it gives {first} the lesson back. Nobody is emailed.
          </Typography>
        ) : started ? (
          <Typography style={{ fontSize: 14.5, lineHeight: "21px", marginBottom: 14 }} data-testid="cancel-lesson-explainer">
            {student}'s lesson{when} has already started. Cancelling it means it won't count, and nobody is emailed.
          </Typography>
        ) : (
          <Typography style={{ fontSize: 14.5, lineHeight: "21px", marginBottom: 14 }} data-testid="cancel-lesson-explainer">
            {student}'s lesson{when} comes off the calendar, and the student and teacher are emailed.
          </Typography>
        )}
        {weekly && (
          <div role="radiogroup" aria-label="This is a weekly lesson" style={{ marginBottom: 14 }} data-testid="cancel-lesson-weekly">
            <Typography style={{ fontSize: 13, fontWeight: 600, color: "#4B5563", marginBottom: 6 }}>
              This is a weekly lesson
            </Typography>
            {[
              { keep: true, label: "Just this one", help: "Next week's lesson stays booked." },
              { keep: false, label: "This and every week after", help: "The weekly lesson stops." },
            ].map((option) => (
              <label key={String(option.keep)} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "6px 0", cursor: "pointer" }}>
                <input
                  type="radio" name="cancel-lesson-weekly" checked={keepWeekly === option.keep} disabled={busy}
                  onChange={() => setKeepWeekly(option.keep)}
                  style={{ width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                />
                <span style={{ fontSize: 14.5, lineHeight: "20px", color: "#1a1a1a" }}>
                  {option.label}
                  <span style={{ display: "block", fontSize: 13, color: "#666666" }}>{option.help}</span>
                </span>
              </label>
            ))}
          </div>
        )}
        <label htmlFor="cancel-lesson-note" style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#4B5563", marginBottom: 6 }}>
          Note (optional)
        </label>
        <textarea
          id="cancel-lesson-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder={started ? "For your records, e.g. Teacher was out sick" : "Goes in the email, e.g. Teacher is out sick"}
          style={{
            width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 10,
            border: "1px solid #D1D5DB", fontSize: 15, fontFamily: "inherit", resize: "vertical",
          }}
        />
        {error && (
          <Typography role="alert" style={{ marginTop: 10, fontSize: 13.5, color: "#B91C1C" }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions style={{ padding: "8px 24px 20px", gap: 8 }}>
        <Button onClick={onClose} disabled={busy} style={{ textTransform: "none" }}>
          Keep it
        </Button>
        <Button
          onClick={submit}
          disabled={busy}
          variant="contained"
          data-testid="confirm-cancel-lesson"
          style={{ textTransform: "none", fontWeight: 600, background: "#B91C1C", color: "#fff", borderRadius: 20, padding: "6px 18px" }}
        >
          {busy ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Cancel lesson"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelLessonModal;
